import React, { useState, useEffect } from 'react';
import { translationsApi } from '../../../lib/api/translations';

interface ContactContent {
  title: string;
  subtitle: string;
  address: string;
  phone1: string;
  phone2: string;
  email: string;
}

const ContactEditor: React.FC = () => {
  const [contactContent, setContactContent] = useState<ContactContent>({
    title: '',
    subtitle: '',
    address: '',
    phone1: '',
    phone2: '',
    email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default values from the website
  const defaultContent = {
    title: 'Võta meiega ühendust',
    subtitle: 'Kui Sul on küsimusi või soovid meie teenuste kohta rohkem teada saada, siis võta meiega julgelt ühendust. Vastame kõigile päringutele 24 tunni jooksul.',
    address: 'Osmussaare tänav 8, 13811 Tallinn, Eesti',
    phone1: '+372 6707 110',
    phone2: '+372 5663 6076',
    email: 'info@forestryest.ee'
  };

  useEffect(() => {
    loadContactContent();
  }, []);

  const loadContactContent = async () => {
    setLoading(true);
    try {
      const keys = [
        'contact.title',
        'contact.subtitle',
        'contact.info.address',
        'contact.info.phone1',
        'contact.info.phone2',
        'contact.info.email'
      ];

      const translations: Record<string, string> = {};
      
      for (const key of keys) {
        const { data } = await translationsApi.getTranslation(key);
        const shortKey = key.split('.').pop() || key;
        translations[shortKey] = data?.value || defaultContent[shortKey as keyof typeof defaultContent] || '';
      }

      setContactContent({
        title: translations.title,
        subtitle: translations.subtitle,
        address: translations.address,
        phone1: translations.phone1,
        phone2: translations.phone2,
        email: translations.email
      });
    } catch (err) {
      console.error('Failed to load contact content:', err);
      setContactContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleContentSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const translationUpdates = [
        { key: 'contact.title', value: contactContent.title, locale: 'et' },
        { key: 'contact.subtitle', value: contactContent.subtitle, locale: 'et' },
        { key: 'contact.info.address', value: contactContent.address, locale: 'et' },
        { key: 'contact.info.phone1', value: contactContent.phone1, locale: 'et' },
        { key: 'contact.info.phone2', value: contactContent.phone2, locale: 'et' },
        { key: 'contact.info.email', value: contactContent.email, locale: 'et' }
      ];

      const { error } = await translationsApi.upsertTranslations(translationUpdates);

      if (error) {
        setError('Kontakti sisu salvestamine ebaõnnestus');
        return;
      }

      // Trigger website update
      window.dispatchEvent(new CustomEvent('translationsUpdated'));
      
      // Show success message briefly
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successDiv.textContent = 'Kontakti sisu edukalt salvestatud!';
      document.body.appendChild(successDiv);
      setTimeout(() => document.body.removeChild(successDiv), 3000);

    } catch (err) {
      setError('Kontakti sisu salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (field: keyof ContactContent, value: string) => {
    setContactContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Kontakti haldus</h1>
        <p className="mt-2 text-gray-600">
          Halda kõiki kontakti sektsiooni tekste ja kontaktandmeid. Muudatused kajastuvad kohe veebilehel.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Kontakti sisu</h3>
          <p className="text-sm text-gray-500 mt-1">
            Halda kõiki kontakti sektsioonis kuvatavaid tekste ja kontaktandmeid
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sektsiooni pealkiri
            </label>
            <input
              type="text"
              value={contactContent.title}
              onChange={(e) => handleContentChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Võta meiega ühendust"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sektsiooni alampealkiri
            </label>
            <textarea
              value={contactContent.subtitle}
              onChange={(e) => handleContentChange('subtitle', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Kui Sul on küsimusi või soovid meie teenuste kohta rohkem teada saada..."
            />
          </div>

          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Kontaktandmed</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aadress
                </label>
                <input
                  type="text"
                  value={contactContent.address}
                  onChange={(e) => handleContentChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Osmussaare tänav 8, 13811 Tallinn, Eesti"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon 1
                  </label>
                  <input
                    type="text"
                    value={contactContent.phone1}
                    onChange={(e) => handleContentChange('phone1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+372 6707 110"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon 2
                  </label>
                  <input
                    type="text"
                    value={contactContent.phone2}
                    onChange={(e) => handleContentChange('phone2', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="+372 5663 6076"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posti aadress
                </label>
                <input
                  type="email"
                  value={contactContent.email}
                  onChange={(e) => handleContentChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="info@forestryest.ee"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t">
            <button
              onClick={handleContentSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvestamine...
                </>
              ) : (
                'Salvesta kontakti sisu'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactEditor;